/**
 * AudioManager Adapter - Compatibility Layer
 * 
 * Provides the old AudioManager API while using the new AudioSystemController underneath.
 * This allows gradual migration without breaking existing code.
 * 
 * @deprecated Use AudioSystemController directly. This adapter will be removed after migration.
 */

import { audioSystemController } from './AudioSystemController';

/**
 * Compatibility adapter that provides the old AudioManager interface
 * while using the new AudioSystemController system
 */
class AudioManagerAdapter {
  /**
   * Check if audio is ready
   */
  isReady(): boolean {
    return audioSystemController.isReady();
  }

  /**
   * Get the main audio context (uses compatibility method)
   */
  getMainAudioContext(): AudioContext | null {
    return audioSystemController.getMainAudioContext();
  }

  /**
   * Alias for getMainAudioContext
   */
  getAudioContext(): AudioContext | null {
    return this.getMainAudioContext();
  }

  /**
   * Get the main master gain node (uses compatibility method)
   */
  getMainMasterGain(): GainNode | null {
    return audioSystemController.getMainMasterGain();
  }

  /**
   * Alias for getMainMasterGain
   */
  getMasterGain(): GainNode | null {
    return this.getMainMasterGain();
  }

  /**
   * Get the reverb node (compatibility method)
   * Returns the reverb node from ReverbProcessor if available
   */
  getReverbNode(): ConvolverNode | null {
    const reverbProcessor = audioSystemController.getReverbProcessor();
    if (reverbProcessor) {
      return reverbProcessor.getNode();
    }
    // Return null if reverb not initialized (non-critical)
    return null;
  }

  /**
   * Get the Sabian audio context (uses compatibility method)
   */
  getSabianAudioContext(): AudioContext | null {
    return audioSystemController.getSabianAudioContext();
  }

  /**
   * Get the Sabian master gain node (uses compatibility method)
   */
  getSabianMasterGain(): GainNode | null {
    return audioSystemController.getSabianMasterGain();
  }

  /**
   * Initialize audio system (compatibility method)
   */
  async initialize(): Promise<void> {
    // Initialize with user interaction flag
    // The adapter assumes user interaction has occurred
    await audioSystemController.initialize(true);
  }

  /**
   * Set master volume (compatibility method)
   * Old API uses linear gain (0-1), converts to dB for new API
   */
  setMasterVolume(volume: number): void {
    // Old API uses linear gain (0-1), new API uses dB (-40 to 0)
    // Convert linear to dB: dB = 20 * log10(gain)
    const clampedGain = Math.max(0, Math.min(1, volume));
    const volumeDb = clampedGain > 0 ? 20 * Math.log10(clampedGain) : -40;
    
    audioSystemController.setMainVolume(volumeDb);
  }

  /**
   * Set main volume (alias)
   */
  setMainVolume(volume: number): void {
    this.setMasterVolume(volume);
  }

  /**
   * Get current master volume (compatibility method)
   * Returns linear gain (0-1) for compatibility
   */
  getMasterVolume(): number {
    const mainEngine = audioSystemController.getMainEngine();
    if (mainEngine) {
      const masterGain = mainEngine.getMasterGain();
      if (masterGain) {
        // Return linear gain value (0-1)
        return masterGain.gain.value;
      }
    }
    return 0.7; // Default fallback (linear gain)
  }

  /**
   * Register callback for when audio initializes (compatibility method)
   */
  onInitialized(callback: () => void): () => void {
    return audioSystemController.onInitialized(() => {
      callback();
    });
  }

  /**
   * Cleanup and dispose audio resources (compatibility method)
   */
  async dispose(): Promise<void> {
    await audioSystemController.cleanup();
  }
}

// Export singleton instance matching old API
export const audioManager = new AudioManagerAdapter();

// Auto-initialize on first user interaction (matching old behavior)
if (typeof window !== 'undefined') {
  let autoInitialized = false;
  
  const autoInitialize = () => {
    if (!autoInitialized) {
      autoInitialized = true;
      audioManager.initialize().catch(err => {
        console.warn('🎵 Auto-initialization failed:', err);
      });
      
      // Remove listeners after first initialization
      document.removeEventListener('click', autoInitialize);
      document.removeEventListener('touchstart', autoInitialize);
      document.removeEventListener('keydown', autoInitialize);
    }
  };
  
  document.addEventListener('click', autoInitialize);
  document.addEventListener('touchstart', autoInitialize);
  document.addEventListener('keydown', autoInitialize);
}

